import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import GetInTouch from './GetInTouch';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import AboutSection from './AboutMe';
import Opinie from './components/Main/Reviews';
import { db } from '../../firebase';
import { collection, getDocs } from 'firebase/firestore';
import heroImage from '../../img/max.png'; // Zostaw jedno najlepsze klimatyczne zdjęcie

function Main({ contentMap }) {
  const { local = 'olkusz' } = useParams();
  const locationData = contentMap[local] || contentMap['olkusz'];

  const [posts, setPosts] = useState([]);

  const fetchPosts = async () => {
    const snap = await getDocs(collection(db, 'blog'));
    const data = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    data.sort((a, b) => {
      if (!a.createdAt || !b.createdAt) return 0;
      return b.createdAt.seconds - a.createdAt.seconds;
    });
    setPosts(data);
  };

  useEffect(() => {
    fetchPosts();
  }, []);

  useEffect(() => {
    document.title = locationData.title;
  }, [locationData.title]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1, transition: { duration: 1.2 } }}
      exit={{ opacity: 0 }}
      className="bg-[#0f0a07] text-[#f5f1ea]"
    >
      <Helmet>
        <title>{locationData.title}</title>
        <meta property="og:title" content={locationData.title} />
        <meta name="description" content={locationData.description} />
        <meta property="og:type" content="website" />
      </Helmet>

      {/* HERO */}
      <section className="min-h-screen flex flex-col lg:flex-row items-center justify-center px-6 lg:px-20 gap-16 bg-gradient-to-b from-[#1c1410] via-[#2a1f17] to-[#0f0a07] pt-24 pb-24">
        
        <motion.div
          className="flex-1 max-w-xl"
          initial={{ opacity: 0, x: -60 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1 className="text-5xl lg:text-6xl font-serif font-bold leading-tight mb-6">
            Ceremonie, które oczyszczają <span className="text-[#c6a15b] italic">ciało i umysł</span>
          </h1>

          <h2 className="text-2xl lg:text-3xl mb-6 font-light">
            Profesjonalny Saunamistrz – {locationData.place}
          </h2>

          <p className="text-lg leading-relaxed opacity-90 mb-8">
            Tworzę autorskie rytuały saunowe oparte na tradycji, aromaterapii
            i świadomej pracy z temperaturą. Każda ceremonia to doświadczenie,
            które pozwala zwolnić, oczyścić się i odzyskać energię.
          </p>

          <div className="flex flex-wrap gap-4">
            <a
              href="#kontakt"
              className="bg-[#c6a15b] px-8 py-4 text-lg font-medium tracking-wide text-[#1a140f] hover:bg-[#b89247] transition rounded shadow-lg"
            >
              Zarezerwuj rytuał
            </a>

            <Link
              to="/projekty"
              className="border border-[#c6a15b] px-8 py-4 text-lg font-medium tracking-wide text-[#c6a15b] hover:bg-[#c6a15b] hover:text-[#1a140f] transition rounded"
            >
              Zobacz ceremonie
            </Link>
          </div>
        </motion.div>

        {/* Jedno klimatyczne zdjęcie */}
        <motion.div
          className="flex-1 max-w-lg"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3, duration: 0.8 }}
        >
          <img
            src={heroImage}
            alt="Ceremonia saunowa"
            className="w-full rounded-2xl shadow-2xl object-cover"
          />
        </motion.div>
      </section>

      {/* SEKCAJA WARTOŚCI */}
      <section className="py-24 px-6 lg:px-20 bg-[#14100d]">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-4xl font-serif mb-12 text-[#c6a15b]">
            Czym jest prawdziwa ceremonia saunowa?
          </h2>

          <div className="grid md:grid-cols-3 gap-12 text-left">
            <div>
              <h3 className="text-xl font-semibold mb-4">🌿 Aromaterapia</h3>
              <p className="opacity-80">
                Naturalne olejki eteryczne dobrane do intencji seansu –
                relaks, oczyszczenie, energia.
              </p>
            </div>

            <div>
              <h3 className="text-xl font-semibold mb-4">🔥 Praca z temperaturą</h3>
              <p className="opacity-80">
                Świadome budowanie intensywności, bezpiecznie i z wyczuciem.
              </p>
            </div>

            <div>
              <h3 className="text-xl font-semibold mb-4">🧘 Rytuał</h3>
              <p className="opacity-80">
                To nie tylko ciepło – to doświadczenie prowadzone
                od początku do końca.
              </p>
            </div>
          </div>
        </div>
      </section>

      <AboutSection />
      <Opinie />

      {/* BLOG */}
      <section className="py-20 px-6 lg:px-20 bg-[#0f0a07]">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-serif text-center mb-12 text-[#c6a15b]">
            Wiedza i inspiracje ze świata sauny
          </h2>

          <div className="grid md:grid-cols-3 gap-8">
            {posts.slice(0, 3).map((post) => (
              <Link
                key={post.id}
                to={`/${local}/blog/${post.slug}`}
                className="border border-[#2a1f17] rounded-xl overflow-hidden hover:border-[#c6a15b] transition bg-[#14100d]"
              >
                {post.coverUrl && (
                  <img
                    src={post.coverUrl}
                    alt={post.title}
                    className="w-full h-56 object-cover"
                  />
                )}
                <div className="p-6">
                  <h3 className="text-xl font-semibold mb-2 text-[#f5f1ea]">
                    {post.title}
                  </h3>
                  {post.createdAt?.seconds && (
                    <p className="text-sm opacity-60">
                      {new Date(post.createdAt.seconds * 1000).toLocaleDateString('pl-PL')}
                    </p>
                  )}
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* KONTAKT */}
      <section id="kontakt" className="bg-[#14100d]">
        <GetInTouch isMain={true} />
      </section>

      {/* FAQ */}
      <section className="max-w-4xl mx-auto px-6 py-24 bg-[#0f0a07]">
  <h2 className="text-4xl font-serif text-center mb-16 text-[#c6a15b]">
    Najczęstsze pytania
  </h2>

  <div className="space-y-6">
    {locationData.faq?.map((item, index) => (
      <details 
        key={index}
        className="border border-[#2a1f17] rounded-xl bg-[#14100d] overflow-hidden"
      >
        <summary className="flex justify-between items-center cursor-pointer p-6 text-2xl font-semibold text-[#c6a15b] hover:text-[#d4b37a]">
          {item.title}
          <span className="text-3xl">+</span>
        </summary>
        <div className="px-6 pb-8 text-gray-300 leading-relaxed opacity-90">
          {item.description}
        </div>
      </details>
    ))}
  </div>
</section>
    </motion.div>
  );
}

export default Main;
